import type { TextFieldProps } from '@mui/material/TextField';
import type { PhoneValue, PhoneCountry, PhoneInputProps } from './types';

import { debounce } from 'es-toolkit';
import { useMemo, useState, useCallback } from 'react';
import PhoneNumberInput from 'react-phone-number-input/input';

import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import { inputBaseClasses } from '@mui/material/InputBase';

import { Iconify } from '../iconify';
import { FlagIcon } from '../flag-icon';

// ----------------------------------------------------------------------

export function PhoneInput({
  sx,
  size,
  label,
  placeholder,
  fullWidth = true,
  variant: variantProp,
  /********/
  value,
  country,
  onChange,
  defaultCountry,
  /********/
  hideSelect,
  ...other
}: PhoneInputProps) {
  const theme = useTheme();
  const variant = variantProp ?? theme.components?.MuiTextField?.defaultProps?.variant;

  const normalizedValue = value ? value.trim().replace(/[\s-]+/g, '') : undefined;

  const [searchCountry, setSearchCountry] = useState('');

  const hasLabel = !!label;

  // India-only launch: force the country globally, regardless of caller props or typed value.
  // We still accept `country` / `defaultCountry` props for compatibility, but ignore them.
  const activeCountry: PhoneCountry = 'IN';
  const isCountryLocked = true;

  const debouncedChange = useMemo(
    () => debounce((inputValue: PhoneValue) => onChange(inputValue), 200),
    [onChange]
  );

  const handleChangeInput = useCallback(
    (inputValue: PhoneValue) => {
      debouncedChange(inputValue ?? '');
    },
    [debouncedChange]
  );

  const handleClearInput = useCallback(() => {
    handleChangeInput('' as PhoneValue);
  }, [handleChangeInput]);

  const handleSearchCountry = useCallback((inputQuery: string) => {
    setSearchCountry(inputQuery);
  }, []);

  const renderSelect = () => {
    const sx = {
      pl: variant === 'standard' ? 0 : 1.5,
      ...(variant === 'standard' && hasLabel && { mt: size === 'small' ? '16px' : '20px' }),
      ...((variant === 'filled' || variant === 'outlined') && { mt: size === 'small' ? '8px' : '16px' }),
      ...(variant === 'filled' && hasLabel && { mt: size === 'small' ? '21px' : '25px' }),
    } as const;

    return (
      <Box
        sx={[
          {
            zIndex: 9,
            display: 'flex',
            position: 'absolute',
            alignItems: 'center',
            justifyContent: 'flex-start',
            width: 'var(--popover-button-width)',
            height: 'var(--popover-button-height)',
            pointerEvents: 'none',
            gap: 0.75,
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        <FlagIcon
          code={activeCountry}
          sx={{
            borderRadius: 0.75,
            width: 'calc(var(--popover-button-height) * 1.3)',
            height: 'var(--popover-button-height)',
          }}
        />

        <Box
          component="span"
          sx={(theme) => ({
            height: 20,
            ml: 'auto',
            width: '1px',
            bgcolor: theme.vars.palette.divider,
          })}
        />
      </Box>
    );
  };

  // Keeping this for potential future re-enable of selection.
  const _unusedCountrySelectionProps = useMemo(
    () => ({ country, defaultCountry, searchCountry, onSearchCountry: handleSearchCountry }),
    [country, defaultCountry, searchCountry, handleSearchCountry]
  );

  const renderInput = () => {
    const textFieldProps: Omit<TextFieldProps, 'value' | 'onChange'> = {
      size,
      label,
      variant,
      fullWidth,
      hiddenLabel: !label,
      placeholder: placeholder ?? 'Enter phone number',
      slotProps: {
        inputLabel: { shrink: true },
        input: {
          endAdornment: normalizedValue && (
            <InputAdornment position="end">
              <IconButton size="small" edge="end" onClick={handleClearInput}>
                <Iconify width={16} icon="mingcute:close-line" />
              </IconButton>
            </InputAdornment>
          ),
        },
      },
    };

    const phoneInputProps: PhoneInputProps = {
      value: normalizedValue,
      onChange: handleChangeInput,
      inputComponent: CustomInput,
      country: activeCountry,
    };

    return <PhoneNumberInput {...textFieldProps} {...phoneInputProps} {...other} />;
  };

  const baseButtonWidth = variant === 'standard' ? '48px' : '60px';
  const disabledButtonWidth = `calc(${baseButtonWidth} - 16px)`;
  const buttonWidth = isCountryLocked ? disabledButtonWidth : baseButtonWidth;

  return (
    <Box
      sx={[
        {
          '--popover-button-mr': '12px',
          '--popover-button-height': '22px',
          '--popover-button-width': buttonWidth,
          position: 'relative',
          ...(fullWidth && { width: 1 }),
          ...(!hideSelect && {
            [`& .${inputBaseClasses.input}`]: {
              pl: 'calc(var(--popover-button-width) + var(--popover-button-mr))',
            },
          }),
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {!hideSelect && renderSelect()}
      {renderInput()}
    </Box>
  );
}

// ----------------------------------------------------------------------

function CustomInput({ ref, ...other }: TextFieldProps) {
  return <TextField inputRef={ref} {...other} />;
}

// ----------------------------------------------------------------------

// parseCountryFromPhone intentionally removed (India-only launch).
